/**
 * TODO будет перенесено в утилиты и покрыто тестами в https://cianru.atlassian.net/browse/CD-38432
 */
import { mergeStyles } from '@cian/utils';

import { Falsy } from '../../../../types';
import { translateX, transform, translateY, scale } from '../../popup_gallery/css';
import styles from '../index.css';
import { IMovingState, ISlide, SliderState, Transition, ICoords, Direction, IContainerSize } from '../types';

export function normalizeZoom(zoom: number) {
  return Math.min(3, Math.max(1, zoom));
}

export function getCoordsFromTouch(touch: React.Touch): ICoords {
  return {
    x: touch.clientX,
    y: touch.clientY,
  };
}

export function getDistanceBetweenTwoCoords(one: ICoords, two: ICoords) {
  const deltaX = Math.abs(one.x - two.x);
  const deltaY = Math.abs(one.y - two.y);

  return Math.sqrt(deltaX ** 2 + deltaY ** 2);
}

export function getCoordsFromTouches(touches: React.TouchList): ICoords | undefined {
  if (!touches || touches.length > 1 || !touches[0]) {
    return undefined;
  }

  return getCoordsFromTouch(touches[0]);
}

export function getCurrentSlide(state: SliderState) {
  return state.slides[state.currentSlideIndex];
}

export function getPrevSlideIndex(state: SliderState) {
  const currentSlideIsFirst = state.currentSlideIndex === 0;

  return currentSlideIsFirst ? state.slides.length - 1 : state.currentSlideIndex - 1;
}

export function getNextSlideIndex(state: SliderState) {
  const currentSlideIsLast = state.currentSlideIndex === state.slides.length - 1;

  return currentSlideIsLast ? 0 : state.currentSlideIndex + 1;
}

export function isTransitionTo(direction: Direction, state: SliderState) {
  return (
    state.action === 'transition' && state.transition.type === 'to-slide' && state.transition.direction === direction
  );
}

function resetZoomForAllSlides(slides: ISlide[]): ISlide[] {
  return slides.map(slide => ({ ...slide, zoom: 1 }));
}

export function toSlideTransition(slideNumber: number, direction: Direction): Transition {
  return { type: 'to-slide', slideNumber, direction };
}

export function toPrevSlideTransition(state: SliderState): Transition {
  return toSlideTransition(getPrevSlideIndex(state), 'right');
}

export function toNextSlideTransition(state: SliderState): Transition {
  return toSlideTransition(getNextSlideIndex(state), 'left');
}

export function toCurrentSlideTransition(state: SliderState): Transition {
  return toSlideTransition(state.currentSlideIndex, 'none');
}

export function toCoordsTransition(coords: ICoords): Transition {
  return { type: 'to-coords', coords };
}

export function jumpToSlide(state: SliderState, slideNumber: number): SliderState {
  return {
    action: 'waiting',
    transition: undefined,
    currentSlideIndex: slideNumber,
    offsetCoords: { x: 0, y: 0 },
    touchStartCoords: { x: 0, y: 0 },
    currentTouchCoords: { x: 0, y: 0 },
    slides: slideNumber !== state.currentSlideIndex ? resetZoomForAllSlides(state.slides) : state.slides,
  };
}

/* Суммарное увеличение в пискелях с обоих сторон
 * http://s.csssr.ru/U09LGPMEU/20170217093838.png
 */
export function getZoomOffset(slideState: ISlide) {
  return slideState.size && (slideState.zoom - 1) * slideState.size.width;
}

function absMoreThan10Or0(num: number) {
  return Math.abs(num) > 10 ? num : 0;
}

export function getXOffset(state: IMovingState) {
  return absMoreThan10Or0(state.currentTouchCoords.x - state.touchStartCoords.x);
}

export function getYOffset(state: IMovingState) {
  return absMoreThan10Or0(state.currentTouchCoords.y - state.touchStartCoords.y);
}

export function getSlideStyles(style: React.CSSProperties, state: SliderState) {
  return mergeStyles(styles['slide'], state.action === 'transition' && styles['transition'], style);
}

export function getSlideCommonTransform(state: IMovingState): (string | Falsy)[] {
  const xOffset = state.action === 'move' && getXOffset(state);

  return [
    isTransitionTo('right', state) && translateX(100, '%'),
    isTransitionTo('left', state) && translateX(-100, '%'),
    xOffset && translateX(xOffset, 'px'),
    state.offsetCoords && translateX(state.offsetCoords.x, 'px'),
  ];
}
export function normalizeYOffset(currentSlideState: ISlide, originalYOffset: number, containerSize?: IContainerSize) {
  // Максимальное допустимое смещение по оси y, если высота картинки меньше высоты контейнера
  // http://s.csssr.ru/U09LGPMEU/20170220083948.png
  const maxYOffset = currentSlideState.size && (currentSlideState.size.height * (currentSlideState.zoom - 1)) / 2;

  // Разница между высотой контейнера и высотой картинки
  const withContainerYDiff =
    containerSize && currentSlideState.size
      ? (currentSlideState.size.height * currentSlideState.zoom - containerSize.height) / 2
      : 0;

  const normalizedWithContainerYDiff = withContainerYDiff > 0 ? withContainerYDiff : 0;

  return maxYOffset
    ? originalYOffset >= 0
      ? Math.min(originalYOffset, maxYOffset, normalizedWithContainerYDiff)
      : Math.max(originalYOffset, -maxYOffset, -normalizedWithContainerYDiff)
    : 0;
}

export function getSlidesStyles(
  state: SliderState,
  yOffset: number,
  containerSize: IContainerSize | undefined,
  offsetAroundZoomed: number | undefined,
) {
  const nextSlideIndex = getNextSlideIndex(state);
  const currentSlideState = getCurrentSlide(state);
  const slideCommonTransform: (string | Falsy)[] = getSlideCommonTransform(state as IMovingState);

  const prevSlideStyle = transform([
    ...slideCommonTransform,
    translateX(-100, '%'),
    state.action !== 'transition' && offsetAroundZoomed && translateX(-offsetAroundZoomed, 'px'),
  ]);

  const currentSlideStyle = transform([
    ...slideCommonTransform,
    yOffset && currentSlideState.zoom > 1 && translateY(yOffset, 'px'),
    state.action === 'transition' && [
      state.transition.type === 'to-slide' &&
        offsetAroundZoomed &&
        translateX((state.transition.slideNumber === nextSlideIndex ? -1 : 1) * offsetAroundZoomed, 'px'),
      state.transition.type === 'to-coords' && [
        translateX(state.transition.coords.x, 'px'),
        translateY(normalizeYOffset(currentSlideState, state.transition.coords.y, containerSize), 'px'),
      ],
    ],
    scale(currentSlideState.zoom),
  ]);

  const nextSlideStyle = transform([
    ...slideCommonTransform,
    translateX(100, '%'),
    state.action !== 'transition' && offsetAroundZoomed && translateX(offsetAroundZoomed, 'px'),
  ]);

  return {
    prevSlideStyle,
    currentSlideStyle,
    nextSlideStyle,
  };
}

export const getZoomTransition =
  (changeSlideDistance: number) =>
  (state: SliderState, containerSize: IContainerSize): Transition => {
    const currentSlide = getCurrentSlide(state);
    const offsetCoords =
      state.action === 'transition' && state.transition.type === 'to-coords'
        ? state.transition.coords
        : state.offsetCoords;

    if (!containerSize || !currentSlide.size) {
      return toCoordsTransition(offsetCoords);
    }

    const slideWidth = currentSlide.size.width * currentSlide.zoom;
    const oneSideOffset = (slideWidth - containerSize.width) / 2;

    const rightOffset = -oneSideOffset - offsetCoords.x;
    const leftOffset = oneSideOffset - offsetCoords.x;

    if (offsetCoords.x < 0) {
      if (rightOffset > changeSlideDistance && state.slides.length > 1) {
        return toNextSlideTransition(state);
      } else if (rightOffset > 0 && oneSideOffset >= 0) {
        return toCoordsTransition({ x: offsetCoords.x + rightOffset, y: offsetCoords.y });
      }
    } else {
      if (leftOffset < -changeSlideDistance && state.slides.length > 1) {
        return toPrevSlideTransition(state);
      } else if (leftOffset < 0 && oneSideOffset >= 0) {
        return toCoordsTransition({ x: offsetCoords.x + leftOffset, y: offsetCoords.y });
      }
    }

    return oneSideOffset < 0 ? toCoordsTransition({ x: 0, y: offsetCoords.y }) : toCoordsTransition(offsetCoords);
  };

/* Суммарный отступ от края слайдера с обоих сторон
 * http://s.csssr.ru/U09LGPMEU/20170217094001.png
 */
function getSpaceAround(slideState: ISlide, containerSize?: IContainerSize) {
  return containerSize && slideState.size && containerSize.width - slideState.size.width;
}

/* Вокруг изображения может оставаться пустое пространство
 * http://s.csssr.ru/U09LGPMEU/20170216160336.png
 * Пока нет зума, всё в порядке, но когда зум появляется, нужно учитывать увеличение
 * и делать отступы у соседних изображений меньше
 * zoom = 1.2 http://s.csssr.ru/U09LGPMEU/20170216163034.png
 * zoom = 1.3 http://s.csssr.ru/U09LGPMEU/20170216163058.png
 * zoom = 1.4 http://s.csssr.ru/U09LGPMEU/20170216163118.png
 */
export function getOffsetForZoomed(slideState: ISlide, containerSize?: IContainerSize) {
  const zoomOffset = getZoomOffset(slideState);
  const spaceAround = getSpaceAround(slideState, containerSize);

  return zoomOffset !== undefined && spaceAround !== undefined
    ? Math.max(0, (zoomOffset - spaceAround) / 2)
    : undefined;
}

export function getTouchOffsetCoords(state: IMovingState) {
  const x = state.offsetCoords.x + getXOffset(state);
  const y = state.offsetCoords.y + getYOffset(state);
  const touchOffsetCoords = { x, y };

  return touchOffsetCoords;
}

export function getTransition(state: SliderState, containerSize: IContainerSize): Transition {
  const SWIPE_DISTANCE = 20;
  const CHANGE_SLIDE_DISTANCE = 20;
  const currentSlide = getCurrentSlide(state);

  if (state.action !== 'move' || !containerSize || !currentSlide.size) {
    return toCoordsTransition(state.offsetCoords);
  }

  const { currentTouchCoords, touchStartCoords } = state;

  if (currentSlide.zoom > 1) {
    return getZoomTransition(CHANGE_SLIDE_DISTANCE)(state, containerSize);
  }

  if (state.slides.length > 1) {
    if (getXOffset(state) > SWIPE_DISTANCE) {
      return toPrevSlideTransition(state);
    }
    if (touchStartCoords.x - currentTouchCoords.x > SWIPE_DISTANCE) {
      return toNextSlideTransition(state);
    }
  }

  return toCurrentSlideTransition(state);
}
